import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  PhoneIcon,
  ClockIcon,
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  CalendarDaysIcon,
  HomeIcon,
  CreditCardIcon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
  BookmarkIcon,
  PlusCircleIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { supabase, startSessionHeartbeat, stopSessionHeartbeat, getAdminStatusCached, clearAdminStatusCache, ensurePublicUser } from './lib/supabase';
import AuthModal from './AuthModal';

function Header() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const topBarRef = useRef(null);

  useEffect(() => {
    // Initial session check — verify with server, not just cached session
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          // Verify session is actually valid by checking with the server
          const { data: { user: verifiedUser }, error } = await supabase.auth.getUser();
          if (error || !verifiedUser) {
            // Session is stale/expired — clear it
            console.warn('Stale session detected, clearing...');
            await supabase.auth.signOut();
            setUser(null);
            setUserProfile(null);
            return;
          }
          setUser(verifiedUser);
          await ensurePublicUser(verifiedUser);
          await fetchUserProfile(verifiedUser.email);
          startSessionHeartbeat();
        }
      } catch (error) {
        console.error('Initial session check error:', error);
      }
    };
    initAuth();

    // Auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip silent token refresh — session is already valid
      if (event === 'TOKEN_REFRESHED') return;

      if (session?.user) {
        setUser(session.user);
        await ensurePublicUser(session.user);
        await fetchUserProfile(session.user.email);
        startSessionHeartbeat();
      } else {
        setUser(null);
        setUserProfile(null);
        stopSessionHeartbeat();
      }
    });

    return () => {
      subscription.unsubscribe();
      stopSessionHeartbeat();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const topBarHeight = topBarRef.current?.offsetHeight || 40;
      setIsSticky(window.scrollY > topBarHeight);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const closeMenu = useCallback(() => {
    setIsMenuClosing(true);
    setTimeout(() => {
      setIsMenuOpen(false);
      setIsMenuClosing(false);
    }, 280);
  }, []);

  const fetchUserProfile = async (email) => {
    const { data, error } = await getAdminStatusCached(email);
    if (!error && data?.[0]) {
      setUserProfile(data[0]);
    }
  };

  const handleSignOut = async () => {
    setUser(null);
    setUserProfile(null);
    setShowUserMenu(false);
    stopSessionHeartbeat();
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn('Sign out API error (session may already be expired):', e);
    }
    clearAdminStatusCache();
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) { /* silent */ }
    window.location.replace(window.location.pathname || '/');
  };

  const navItems = [
    { path: '/', label: 'Home', icon: HomeIcon },
    { path: '/booking', label: 'Booking', icon: CalendarDaysIcon },
    { path: '/membership', label: 'Membership', icon: UserGroupIcon },
    { path: '/contact-payment', label: 'Contact & Payment', icon: CreditCardIcon },
  ];

  const isActive = (path) => location.pathname === path;

  const getUserInitials = () => {
    const name = userProfile?.full_name || user?.email || '';
    if (name.includes('@')) return name[0].toUpperCase();
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <>
      <header className="bg-white">
        {/* Top Bar */}
        <div ref={topBarRef} className="bg-gradient-to-r from-green-700 via-green-600 to-emerald-600 text-white">
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-center h-9 text-xs sm:text-sm">
              <div className="flex items-center gap-4 sm:gap-6">
                <a href="tel:+918080517517" className="flex items-center gap-1.5 hover:text-green-200 transition-colors">
                  <PhoneIcon className="h-3.5 w-3.5" />
                  <span>+91 8080517517</span>
                </a>
                <div className="hidden sm:flex items-center gap-1.5 text-green-100">
                  <ClockIcon className="h-3.5 w-3.5" />
                  <span>5 AM – 9 PM | All Days</span>
                </div>
              </div>
              <a
                href="https://www.google.com/maps?q=12.8628313,80.0860179"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-1.5 hover:text-green-200 transition-colors"
              >
                <MapPinIcon className="h-3.5 w-3.5" />
                <span>Urapakkam, Chennai</span>
              </a>
            </div>
          </div>
        </div>

        {/* Main Header - always sticky-ready */}
        <div
          className={`bg-white/95 backdrop-blur-md border-b z-50 transition-shadow duration-300 ${
            isSticky
              ? 'fixed top-0 left-0 right-0 shadow-lg animate-slideDown border-gray-200/60'
              : 'relative border-gray-100 shadow-sm'
          }`}
        >
          <div className="container mx-auto px-4">
            <div className="flex items-center h-14 sm:h-16">
              {/* Logo - Left */}
              <Link to="/" className="flex items-center gap-2 sm:gap-2.5 flex-shrink-0 mr-auto">
                <img
                  src="/images/SquareLogo.jpg"
                  alt="Best Badminton Club"
                  className="h-8 sm:h-10 w-auto rounded-lg object-contain"
                />
                <div className="min-w-0">
                  <h1 className="text-sm sm:text-lg font-bold text-gray-800 truncate leading-tight">
                    Best Badminton Club
                  </h1>
                  <p className="text-[9px] sm:text-[11px] text-gray-400 font-medium tracking-wider uppercase hidden sm:block">
                    Premium Sports Facility
                  </p>
                </div>
              </Link>

              {/* Desktop: Nav + Profile (right-aligned) */}
              <div className="hidden lg:flex items-center gap-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`relative px-3.5 xl:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      isActive(item.path)
                        ? 'text-green-700 bg-green-50'
                        : 'text-gray-600 hover:text-green-700 hover:bg-gray-50'
                    }`}
                  >
                    {item.label}
                    {isActive(item.path) && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-green-600 rounded-full" />
                    )}
                  </Link>
                ))}

                {/* Divider */}
                <div className="w-px h-6 bg-gray-200 mx-2" />

                {/* Profile / Sign In */}
                {user ? (
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className={`h-9 w-9 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-xs font-bold shadow-sm transition-all duration-200 hover:scale-105 hover:shadow-md ${
                        showUserMenu ? 'ring-2 ring-green-300 ring-offset-2 scale-105' : ''
                      }`}
                      title={userProfile?.full_name || user.email}
                    >
                      {getUserInitials()}
                    </button>

                    {showUserMenu && (
                      <div className="absolute right-0 mt-3 w-56 bg-white rounded-xl shadow-xl z-50 border border-gray-200 overflow-hidden animate-smooth-drop">
                        <div className="px-4 py-3 bg-gradient-to-br from-green-50 to-emerald-50 border-b border-green-100">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                              {getUserInitials()}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">{userProfile?.full_name || 'User'}</p>
                              <p className="text-xs text-gray-500 truncate">{user.email}</p>
                            </div>
                          </div>
                        </div>
                        <div className="py-1">
                          <Link
                            to="/my-bookings"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            onClick={() => setShowUserMenu(false)}
                          >
                            <BookmarkIcon className="h-4 w-4 text-gray-400" />
                            My Bookings
                          </Link>
                          <Link
                            to="/booking"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            onClick={() => setShowUserMenu(false)}
                          >
                            <PlusCircleIcon className="h-4 w-4 text-gray-400" />
                            New Booking
                          </Link>
                          {userProfile?.is_admin && (
                            <Link
                              to="/admin"
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-purple-700 hover:bg-purple-50 transition-colors font-medium"
                              onClick={() => setShowUserMenu(false)}
                            >
                              <ShieldCheckIcon className="h-4 w-4 text-purple-500" />
                              Admin Dashboard
                            </Link>
                          )}
                        </div>
                        <div className="border-t border-gray-100">
                          <button
                            onClick={handleSignOut}
                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <ArrowRightOnRectangleIcon className="h-4 w-4" />
                            Sign Out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-full text-sm font-semibold hover:bg-green-700 transition-all duration-200 shadow-sm hover:shadow-md hover:scale-105"
                  >
                    <UserIcon className="h-4 w-4" />
                    Sign In
                  </button>
                )}
              </div>

              {/* Mobile: Profile + Hamburger */}
              <div className="flex lg:hidden items-center gap-2">
                {user && (
                  <div
                    className="h-8 w-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-[10px] font-bold shadow-sm cursor-pointer"
                    onClick={() => setIsMenuOpen(true)}
                    title={userProfile?.full_name || user.email}
                  >
                    {getUserInitials()}
                  </div>
                )}
                <button
                  className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                  onClick={() => isMenuOpen ? closeMenu() : setIsMenuOpen(true)}
                  aria-label="Toggle menu"
                >
                  <Bars3Icon className="h-6 w-6 text-gray-700" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Slide-in Drawer */}
      {(isMenuOpen || isMenuClosing) && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
              isMenuClosing ? 'opacity-0' : 'opacity-100'
            }`}
            style={{ animation: !isMenuClosing ? 'fadeIn 0.3s ease-out' : undefined }}
            onClick={closeMenu}
          />

          {/* Panel */}
          <div
            className={`absolute top-0 right-0 w-[280px] sm:w-[320px] h-full bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
              isMenuClosing ? 'translate-x-full' : 'translate-x-0'
            }`}
            style={{ animation: !isMenuClosing ? 'slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)' : undefined }}
          >
            {/* Panel Header */}
            <div className="flex items-center justify-between px-5 h-14 sm:h-16 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <img
                  src="/images/SquareLogo.jpg"
                  alt="Logo"
                  className="h-7 sm:h-8 w-auto rounded-lg object-contain"
                />
                <span className="font-bold text-gray-800 text-sm sm:text-base">Menu</span>
              </div>
              <button
                onClick={closeMenu}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* User Card */}
            {user && (
              <div
                className="mx-4 mt-4 p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100"
                style={{ animation: 'fadeInUp 0.35s ease-out 0.1s both' }}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-sm font-bold shadow-sm flex-shrink-0">
                    {getUserInitials()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">
                      {userProfile?.full_name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Nav Links */}
            <nav className="flex-1 overflow-y-auto px-4 py-4">
              <div className="space-y-1">
                {navItems.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={closeMenu}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                        isActive(item.path)
                          ? 'bg-green-50 text-green-700 shadow-sm'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 active:bg-gray-100'
                      }`}
                      style={{ animation: `fadeInUp 0.35s ease-out ${0.05 * (idx + 1) + 0.1}s both` }}
                    >
                      <Icon className={`h-5 w-5 flex-shrink-0 ${isActive(item.path) ? 'text-green-600' : 'text-gray-400'}`} />
                      {item.label}
                      {isActive(item.path) && (
                        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-green-500" />
                      )}
                    </Link>
                  );
                })}
              </div>

              {user && (
                <>
                  <div className="my-4 border-t border-gray-100" />
                  <div className="space-y-1">
                    <Link
                      to="/my-bookings"
                      onClick={closeMenu}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                        isActive('/my-bookings')
                          ? 'bg-green-50 text-green-700 shadow-sm'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 active:bg-gray-100'
                      }`}
                      style={{ animation: 'fadeInUp 0.35s ease-out 0.35s both' }}
                    >
                      <BookmarkIcon className={`h-5 w-5 flex-shrink-0 ${isActive('/my-bookings') ? 'text-green-600' : 'text-gray-400'}`} />
                      My Bookings
                    </Link>
                    {userProfile?.is_admin && (
                      <Link
                        to="/admin"
                        onClick={closeMenu}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                          isActive('/admin')
                            ? 'bg-purple-50 text-purple-700 shadow-sm'
                            : 'text-purple-600 hover:bg-purple-50 active:bg-purple-100'
                        }`}
                        style={{ animation: 'fadeInUp 0.35s ease-out 0.4s both' }}
                      >
                        <ShieldCheckIcon className={`h-5 w-5 flex-shrink-0 ${isActive('/admin') ? 'text-purple-600' : 'text-purple-400'}`} />
                        Admin Dashboard
                      </Link>
                    )}
                  </div>
                </>
              )}
            </nav>

            {/* Panel Footer */}
            <div
              className="flex-shrink-0 px-4 py-4 border-t border-gray-100 bg-gray-50/50"
              style={{ animation: 'fadeInUp 0.35s ease-out 0.3s both' }}
            >
              {user ? (
                <button
                  onClick={() => {
                    handleSignOut();
                    closeMenu();
                  }}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 active:bg-red-200 transition-colors"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                  Sign Out
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowAuthModal(true);
                    closeMenu();
                  }}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl text-sm font-semibold hover:from-green-700 hover:to-emerald-700 active:from-green-800 active:to-emerald-800 transition-all shadow-sm"
                >
                  <UserIcon className="h-4 w-4" />
                  Sign In / Sign Up
                </button>
              )}

              <div className="flex items-center justify-center gap-4 mt-3 text-xs text-gray-400">
                <a href="tel:+918080517517" className="flex items-center gap-1 hover:text-green-600 transition-colors">
                  <PhoneIcon className="h-3 w-3" />
                  +91 8080517517
                </a>
                <span className="text-gray-300">|</span>
                <span className="flex items-center gap-1">
                  <ClockIcon className="h-3 w-3" />
                  5AM–10PM
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spacer for fixed header */}
      {isSticky && <div className="h-14 sm:h-16" />}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={(user) => {
          setUser(user);
          ensurePublicUser(user).then(() => fetchUserProfile(user.email));
        }}
      />
    </>
  );
}

export default Header;
